/**
 * UnauthorizedPage - Access Denied Page
 *
 * Displayed when a user attempts to access a route they don't have
 * permission for. Provides clear feedback and navigation options.
 */

import React from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Home, ArrowLeft, ShieldOff } from 'lucide-react'
import { Button, Heading, Text } from '@agion/ui'

export const UnauthorizedPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'common'])
  const navigate = useNavigate()

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--background)] px-4">
      <div className="max-w-md w-full text-center space-y-8">
        {/* 403 Error Icon */}
        <div className="space-y-4">
          <div className="mx-auto h-24 w-24 text-[var(--muted-foreground)]">
            <ShieldOff className="h-full w-full" />
          </div>
          <Heading level="h1" className="text-6xl text-[var(--muted)]">
            {t('pages:unauthorized.errorCode', '403')}
          </Heading>
          <Heading level="h1" as="h2">
            {t('pages:unauthorized.title', 'Access Denied')}
          </Heading>
          <Text size="lg" color="muted">
            {t('pages:unauthorized.subtitle', "You don't have permission to access this page.")}
          </Text>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/chat">
            <Button
              variant="primary"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
            >
              <Home className="h-4 w-4" />
              {t('pages:unauthorized.backToChat', 'Go to Chat')}
            </Button>
          </Link>
          <Button
            variant="secondary"
            onClick={handleGoBack}
            className="inline-flex items-center justify-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common:back', 'Go Back')}
          </Button>
        </div>

        {/* Help text */}
        <Text size="sm" color="muted">
          {t(
            'pages:unauthorized.helpText',
            'If you believe this is an error, please contact your organization administrator.'
          )}
        </Text>
      </div>
    </div>
  )
}

export default UnauthorizedPage
